import type { EmployeeRole } from "@shared/types";

/**
 * Centralized access control gates.
 *
 * This is the SINGLE SOURCE OF TRUTH for who can do what in the app.
 * RLS policies in the database mirror these gates for defense in depth.
 *
 * ## Convention: {entity}_{show|edit|manage}
 *
 *   show   — view entity (drawer content, page access, sider menu, global search)
 *   edit   — create/edit the entity's core data (modals, INSERT/UPDATE in RLS)
 *   manage — administer related sub-entities, advanced actions
 *
 * ## How gates are enforced
 *
 * | Layer              | Mechanism                              | File                          |
 * |--------------------|----------------------------------------|-------------------------------|
 * | Route pages        | `<RequireRole gate="..." />`           | App.tsx                       |
 * | Sider menu         | `SIDER_KEY_GATES` map                  | CustomSider.tsx               |
 * | Global search      | `gate` field on CommandItem            | GlobalSearchModal.tsx          |
 * | Modals             | `withGate(gate, Inner)` HOC            | useRoleGuard.ts               |
 * | Drawer content     | `withContentGate(gate, Inner)` HOC     | useRoleGuard.ts               |
 * | RLS SELECT         | `is_employee()` on all tables          | DB migrations                 |
 * | RLS INSERT/UPDATE  | `has_any_role(...)` matching _edit gate | DB migrations                 |
 * | RLS DELETE          | `has_any_role('{admin}')` on all tables | DB migrations                 |
 *
 * ## Entity → table mapping (RLS alignment)
 *
 * | Gate prefix        | Tables                                                                    |
 * |--------------------|---------------------------------------------------------------------------|
 * | product            | products                                                                  |
 * | company (edit)     | companies, contacts                                                       |
 * | company (manage)   | company_bank_accounts, company_waste_properties,                          |
 * |                    | company_recycling_processes, company_sales_assignments                     |
 * | invoice            | invoices, invoice_line_items, invoice_links                                |
 * |                    | (invoice_bank_accounts is READ-only for employees — the KSeF pipeline is   |
 * |                    |  its only writer, so it has no _edit gate)                                 |
 * | price_list         | price_lists                                                               |
 * | transaction        | transactions, transaction_transports, transaction_files, transaction_folders|
 * | company_document   | company_documents                                                         |
 * | edpr               | edpr_documents, edpr_records                                              |
 * | employee           | employees                                                                 |
 * | delivery_target    | delivery_targets                                                          |
 * | automation         | jobs                                                                      |
 * | settings           | app_configuration, invoice_approval_rules, invoice_warning_clauses,       |
 * |                    | transaction_rules, reports (developer: all but report SQL edit)  |
 * | template           | template_configs (SELECT/INSERT/UPDATE: admin+management+developer;        |
 * |                    | DELETE: admin)                                                            |
 *
 * ## Tables NOT gated (special handling)
 *
 * | Table              | SELECT          | INSERT/UPDATE    | DELETE     | Reason               |
 * |--------------------|-----------------|------------------|------------|----------------------|
 * | comments           | is_employee()   | is_employee()    | admin      | Any employee comments|
 * | email_sends/attach | is_employee()   | is_employee()    | admin      | Any employee emails  |
 * | reconcile_locks    | is_employee()   | is_employee()    | admin      | SECURITY DEFINER fns |
 * | countries          | is_employee()   | admin            | admin      | Reference data       |
 * | exchange_rates     | is_employee()   | admin            | admin      | Reference data       |
 * | waste_codes        | is_employee()   | admin            | admin      | Reference data       |
 * | sync_metadata      | is_employee()   | admin            | admin      | System               |
 * | user_google_tokens | own token only  | own token        | own token  | OAuth credentials    |
 * | entity_audit_log   | is_employee()   | any (INSERT only)| none       | Audit trail          |
 * | dashboard_*        | is_employee()   | admin+management | admin+mgmt | Dashboard config     |
 * | gmail_watch_state  | is_employee()   | service_role     | service_role| System              |
 * | recycling_processes| is_employee()   | service_role     | service_role| Reference data      |
 * | document_extractions| is_employee() + employee_update | service_role| Pipeline           |
 * | document_jobs      | is_employee()   | service_role     | service_role| Pipeline            |
 * | email_ingestions   | is_employee()   | service_role     | service_role| Pipeline            |
 */
export const ACCESS_GATES = {
  // Products
  product_show:             ["admin", "management", "administration", "logistics", "sales", "developer"],
  product_edit:             ["admin", "management", "administration", "developer"],
  product_manage:           ["admin", "management", "administration", "developer"],

  // Companies
  company_show:             ["admin", "management", "administration", "logistics", "sales", "developer"],
  company_edit:             ["admin", "management", "administration", "logistics", "sales", "developer"],
  company_manage:           ["admin", "management", "administration", "developer"],

  // Invoices — sales READ invoices (their transactions link to them) but never
  // write: `invoice_edit` stays without "sales", and every mutating affordance
  // on the invoice surface is gated on it (see `useGate("invoice_edit")`).
  // RLS already matches: SELECT is `is_employee()`, INSERT/UPDATE is
  // `has_any_role('{admin,management,administration}')`.
  invoice_show:             ["admin", "management", "administration", "sales", "developer"],
  invoice_edit:             ["admin", "management", "administration", "developer"],
  invoice_manage:           ["admin", "management", "administration", "developer"],

  // Price lists
  price_list_show:          ["admin", "management", "administration", "sales", "developer"],
  price_list_edit:          ["admin", "management", "administration", "developer"],
  price_list_manage:        ["admin", "management", "administration", "developer"],

  // Transactions
  transaction_show:         ["admin", "management", "administration", "logistics", "sales", "developer"],
  transaction_edit:         ["admin", "management", "administration", "logistics", "sales", "developer"],
  transaction_manage:       ["admin", "management", "administration", "developer"],

  // Company documents
  company_document_show:    ["admin", "management", "administration", "developer"],
  company_document_edit:    ["admin", "management", "administration", "developer"],
  company_document_manage:  ["admin", "management", "administration", "developer"],

  // EDPR
  edpr_show:                ["admin", "management", "administration", "developer"],
  edpr_edit:                ["admin", "management", "administration", "developer"],
  edpr_manage:              ["admin", "management", "administration", "developer"],

  // Employees
  employee_show:            ["admin", "management", "developer"],
  employee_edit:            ["admin"],
  employee_manage:          ["admin"],

  // Delivery targets
  delivery_target_show:     ["admin", "management", "developer"],
  delivery_target_edit:     ["admin", "management", "developer"],
  delivery_target_manage:   ["admin", "management", "developer"],

  // Automation (document jobs, async jobs)
  automation_show:          ["admin", "management", "administration", "developer"],
  automation_edit:          ["admin", "management", "administration", "developer"],
  automation_manage:        ["admin", "management", "administration", "developer"],
  // Pipeline job rerun — batch "Ponów przetwarzanie" on /dokumenty. Mirrors
  // the rerun-document-job edge fn's own ADMIN_ROLES allowlist exactly.
  pipeline_rerun:           ["admin", "management", "administration", "developer"],

  // Logistics dashboard
  logistics_dashboard_show: ["admin", "management", "logistics", "developer"],

  // Sales dashboard
  sales_dashboard_show:     ["admin", "management", "sales", "developer"],

  // Reports
  reports_show:             ["admin", "management", "developer"],
  reports_edit:             ["admin", "management"],

  // Dashboard (home/sales/logistics layouts and widget config)
  dashboard_edit:           ["admin", "management"],

  // The `developer` role is named after WHO gets it. As sole role it is admin
  // minus: commission data, employee editing, report SQL / dashboard editing,
  // and `bank_send` (transmitting payments to mBank). Every other gate includes
  // it. The DB mirror is migration `*_developer_role_admin_parity.sql` plus
  // `public.is_developer_restricted()` (RESTRICTIVE policies on commission
  // tables).
  //
  // Settings (management sees the section but only reports + templates sub-pages
  // are accessible; the rest stays admin-only via settings_edit)
  settings_show:            ["admin", "management", "developer"],
  settings_edit:            ["admin", "developer"],
  settings_manage:          ["admin", "developer"],
  // Gmail Pipeline + Dziennik zmian — system pages (developer included)
  settings_system_show:     ["admin", "management", "developer"],

  // Email / document templates (template_configs) — management co-owns these
  template_show:            ["admin", "management", "developer"],
  template_edit:            ["admin", "management", "developer"],

  // Commission
  commission_show:          ["admin"],
  commission_edit:          ["admin"],
  commission_manage:        ["admin"],
  // Narrow gate for the "Mój raport" self-service view — sales see their own
  // payee report; management + admin see everything. The admin-only sub-pages
  // (rules, payees, EDPR rates, config) stay gated at commission_edit.
  commission_self_report_show: ["admin", "management", "sales"],

  // Bank (mBank CompanyConnect integration — inbox, batches, statement lines)
  bank_show:                ["admin", "developer"],
  bank_edit:                ["admin", "developer"],
  bank_manage:              ["admin", "developer"],
  // Transmitting payments to mBank — everyone who can reach the send
  // affordance today, never developer.
  bank_send:                ["admin", "management", "administration"],
} as const satisfies Record<string, readonly EmployeeRole[]>;

export type AccessGate = keyof typeof ACCESS_GATES;
